import { createContext, use } from 'react';

import type { CompanyRef } from '../../api';

/**
 * Which company's portal this is.
 *
 * The portal shows one company at a time, always. Almost every user is entitled
 * to exactly one and never sees a choice; a principal entitled to more than one
 * picks, and the pick is a single piece of state that every screen below reads.
 *
 * Three things about how the entitlement is decided:
 *
 *  - It comes from the server. `GET /companies` is scoped by #206 before the
 *    handler runs, so the list is the set of companies this principal may act
 *    inside -- not a list the browser filtered. The principal's own company is
 *    the fallback when that call has not landed or has failed, so a transient
 *    failure does not lock somebody out of their own data -- and so are the
 *    `companies` the session's principal already carries, which is what a user
 *    registered at a company group (#391), who has no own company, starts from.
 *  - It is never in the URL. No path in `src/routes/paths.ts` names a company,
 *    so there is no address to paste that would switch tenants -- and if one
 *    were forged, the header below would still have to match, and the backend
 *    would still refuse.
 *  - It is sent as `X-Company-Id` on every scoped call. For a customer
 *    principal that header is redundant and must equal its own company; the
 *    backend answers `403` when it does not. Sending it means the screens work
 *    unchanged the day a principal may hold more than one company, and means a
 *    bug in this file is a refusal rather than another tenant's data.
 *
 * And one about which company the portal opens on (#392). Browser storage is
 * off limits here (see `session/sessionStore.ts`), so the last choice is
 * remembered on the server, per user, and comes back on the principal as
 * `selected_company` -- after a reload, a new sign-in or on another browser.
 * It is a default and nothing more: {@link initialCompany} honours it only when
 * it is in the entitled list, and the header is still what scopes a request.
 */
export interface CompanyScope {
  /** The company every call below is made inside. */
  readonly company: CompanyRef;
  /** Every company this principal may act inside, the current one included. */
  readonly companies: readonly CompanyRef[];
  readonly selectCompany: (companyId: string) => void;
  /** Headers for a scoped call, ready to spread into a generated operation. */
  readonly headers: { readonly 'X-Company-Id': string };
}

export const CompanyScopeContext = createContext<CompanyScope | null>(null);

export function useCompanyScope(): CompanyScope {
  const scope = use(CompanyScopeContext);
  if (scope === null) {
    throw new Error('useCompanyScope precisa de <CompanyScopeProvider> acima na árvore.');
  }
  return scope;
}

function asRef(company: { id: string; name: string; slug: string }): CompanyRef {
  return { id: company.id, name: company.name, slug: company.slug };
}

/**
 * The companies this principal may act inside.
 *
 * The principal's own company is included even when the list call has not
 * answered, because the session already carries it and waiting would mean a
 * spinner over data we are entitled to read. Ordered by name, which is the
 * order the switcher offers them in.
 */
export function entitledCompanies(
  own: CompanyRef | null | undefined,
  listed: readonly { id: string; name: string; slug: string }[] | undefined,
): readonly CompanyRef[] {
  const byId = new Map<string, CompanyRef>();
  if (own !== null && own !== undefined) {
    byId.set(own.id, asRef(own));
  }
  for (const company of listed ?? []) {
    byId.set(company.id, asRef(company));
  }
  return [...byId.values()].sort(byName);
}

function byName(left: CompanyRef, right: CompanyRef): number {
  return left.name.localeCompare(right.name, 'pt-BR') || left.slug.localeCompare(right.slug);
}

/**
 * The company the portal opens on, before anybody has chosen in this page load.
 *
 * In order: the company the server remembers this person last chose, the
 * principal's own company, the first entitled company by name. Each is taken
 * only from `companies`, so a remembered company that has since left the group
 * is passed over without a request ever being made for it.
 */
export function initialCompany(
  companies: readonly CompanyRef[],
  remembered: CompanyRef | null | undefined,
  own: CompanyRef | null | undefined,
): CompanyRef | undefined {
  return (
    companies.find((company) => company.id === remembered?.id) ??
    companies.find((company) => company.id === own?.id) ??
    companies[0]
  );
}
